import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Archive,
  Check,
  ChevronDown,
  Layers,
  LogOut,
  Settings as SettingsIcon,
} from "lucide-react";

import greenlightLogo from "../assets/images/greenlight-centered.svg";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { rememberSpace, rememberedSpace } from "@/lib/activeSpace";
import { isAdmin } from "@/lib/perms";
import { spacesQueryOptions, useUser } from "@/lib/queries";
import { persister } from "@/lib/queryClient";
import { logout } from "@/lib/session";

export function Header() {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const admin = isAdmin(user);

  // The active space is per-viewer and URL-driven (?space=<id>); the switcher
  // just navigates the board there and remembers the choice. Shown to everyone.
  // useSearch is loose (strict:false) because the Header renders on routes that
  // don't carry a space param (settings, notifications).
  const search = useSearch({ strict: false }) as { space_id?: string };
  const { data: spaces = [] } = useQuery(spacesQueryOptions);
  const activeSpaceId =
    search.space_id ?? rememberedSpace() ?? spaces[0]?.id ?? "";
  const activeSpace = spaces.find((s) => s.id === activeSpaceId);
  const activeLabel = activeSpace?.name ?? activeSpace?.id ?? "Select space";

  const selectSpace = (id: string) => {
    if (id === activeSpaceId) return;
    rememberSpace(id);
    void navigate({ to: "/", search: { space_id: id } });
  };

  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.clear();
      // Also wipe the persisted snapshot so the next user can't see this user's
      // cached board/notifications from localStorage.
      void persister.removeClient();
      void navigate({ to: "/login" });
    },
  });

  const initials = (user.name ?? user.email ?? "?")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="border-b border-border">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-1.5">
        <Link
          to="/"
          className="-ml-1 cursor-pointer rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Go to board"
        >
          <img src={greenlightLogo} alt="Greenlight" className="h-16 w-auto" />
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {spaces.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Layers className="size-4 shrink-0 text-muted-foreground" />
                  <span className="max-w-[12rem] truncate">{activeLabel}</span>
                  <ChevronDown className="size-4 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <DropdownMenuLabel>Spaces</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {spaces.map((space) => (
                  <DropdownMenuItem
                    key={space.id}
                    onClick={() => selectSpace(space.id)}
                    className="justify-between"
                  >
                    {space.name ?? space.id}
                    {space.id === activeSpaceId && <Check className="size-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* +ml-2 offsets the icon button's 8px inset against the flush
              space-switcher edge, so the visible gap matches archive→bell. */}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Archived"
            asChild
            className="ml-2"
          >
            <Link to="/archived">
              <Archive className="size-4" />
            </Link>
          </Button>

          <NotificationsBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="ml-2 flex h-8 cursor-pointer items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Account menu"
              >
                <Avatar className="size-8">
                  {user.avatarUrl && (
                    <AvatarImage src={user.avatarUrl} alt={user.name ?? ""} />
                  )}
                  <AvatarFallback className="text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="size-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              // Don't restore focus to the trigger on close — otherwise clicking
              // outside flashes the focus-visible ring around the avatar/chevron.
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel className="flex flex-col gap-0.5">
                <span>{user.name ?? "—"}</span>
                {user.email && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {user.email}
                  </span>
                )}
              </DropdownMenuLabel>
              {admin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <SettingsIcon />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => signOut.mutate()}
                disabled={signOut.isPending}
              >
                <LogOut />
                {signOut.isPending ? "Signing out…" : "Sign out"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export default Header;
